import { HttpClient } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { BehaviorSubject, lastValueFrom } from 'rxjs';
import { ScreensResponseDto } from 'src/app/shared/dto/pagination/screens-response.dto';
import { ScreenDto } from 'src/app/shared/dto/screen.dto';
import { CustomErrorStatusesEnum } from 'src/app/shared/enums/custom-error-statuses.enum';
import { BaseService } from 'src/app/shared/services/base.service';
import { SocketService } from 'src/app/shared/services/socket.service';
import { Messages } from 'src/app/shared/sockets/enums/messages';
import { Client, formatClientRoom, RoomNames } from 'src/app/shared/sockets/enums/rooms';

@Injectable({
    providedIn: 'root',
})
export class ScreenService extends BaseService {
    private screensSubject = new BehaviorSubject<ScreenDto[]>([]);
    public screens$ = this.screensSubject.asObservable();

    private readonly socketService = inject(SocketService);

    constructor(httpClient: HttpClient) {
        super(httpClient, 'screens');
    }

    async setupWebSocket(): Promise<void> {
        this.screensSubject.next((await this.findAll()).screens);
        this.socketService.conectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.SCREENS_ROOM));
        this.socketService.getNotifications(Messages.SCREENS_CHANGED).subscribe(async () => {
            const response = await this.findAll();
            this.screensSubject.next(response.screens);
        });
    }

    disconnectWebSocket(): void {
        this.socketService.disconnectSocketRoom(formatClientRoom(Client.DEFAULT_CLIENT, RoomNames.SCREENS_ROOM));
    }

    async getByIp(ip: string): Promise<ScreenDto[]> {
        try {
            if (ip === '') {
                throw new Error('Se ha de facilitar una ip válida');
            }

            const request = this.httpClient.get<ScreenDto[]>(this.url + `/ip/${ip}`);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha encontrado ninguna pantalla. Error: ${e.message}`);
        }
    }

    async findAll(): Promise<ScreensResponseDto> {
        try {
            const request = this.httpClient.get<ScreensResponseDto>(`${this.url}/findAll`);

            return await lastValueFrom(request);
        } catch (e: any) {
            if (e.error.statusCode === 401) {
                throw new Error(`${e.error.message}`);
            }

            throw new Error(`No se ha encontrado ninguna pantalla. Error: ${e.error.message}`);
        }
    }

    async create(screen: ScreenDto): Promise<ScreenDto> {
        try {
            if (screen === null || screen === undefined) {
                throw new Error('La pantalla no es válida');
            }

            const request = this.httpClient.post<ScreenDto>(`${this.url}`, screen);

            return await lastValueFrom(request);
        } catch (e: any) {
            if (e.status === CustomErrorStatusesEnum.SCREENS_LIMIT_LICENSE) {
                throw new Error('Tu licencia no permite crear más pantallas.');
            }

            throw new Error(`No se ha podido crear la pantalla. Error: ${e.error.message}`);
        }
    }

    async updateScreen(screenId: number, screen: ScreenDto): Promise<ScreenDto> {
        try {

            if (screenId <= 0) {
                throw new Error('El id de la pantalla ha de ser mayor de 0');
            }

            if (screen === null || screen === undefined) {
                throw new Error('La pantalla no es válida');
            }

            const request = this.httpClient.patch<ScreenDto>(`${this.url}/${screenId}`, screen);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha podido actualizar la pantalla. Error: ${e.message}`);
        }
    }

    async deleteScreen(id: number): Promise<ScreenDto> {
        try {
            if (id <= 0) {
                throw new Error('El id de la pantala ha de ser mayor de 0');
            }

            const request = this.httpClient.delete<ScreenDto>(`${this.url}/${id}`);

            return await lastValueFrom(request);
        } catch (e: any) {
            throw new Error(`No se ha podido eliminar la pantalla. Error: ${e.message}`);
        }
    }
}
